import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class StepFormService {
  private formDataSubject = new BehaviorSubject<any>(null);
  private currentStepSubject = new BehaviorSubject<number>(1);

  formData$ = this.formDataSubject.asObservable();
  currentStep$ = this.currentStepSubject.asObservable();

  private formData: any = {};
  private currentStep = 1;
  private totalSteps = 2;

  // Nova propriedade para controlar persistência entre navegações
  private shouldPersist = false;

  constructor() {
    // Verifica se estamos na mesma sessão de navegação
    const sessionId = sessionStorage.getItem('agendamentoSessionId');
    const savedSessionId = localStorage.getItem('currentAgendamentoSession');

    if (sessionId && sessionId === savedSessionId) {
      const savedData = localStorage.getItem('agendamentoForm');
      if (savedData) {
        this.formData = JSON.parse(savedData);
        this.formDataSubject.next(this.formData);
        this.shouldPersist = true;
      }
    } else {
      // Nova sessão, limpa dados antigos
      this.clearStorage();
    }
  }

  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private clearStorage(): void {
    localStorage.removeItem('agendamentoForm');
    localStorage.removeItem('currentAgendamentoSession');
  }

  initializeSession(): void {
    const sessionId = this.generateSessionId();
    sessionStorage.setItem('agendamentoSessionId', sessionId);
    localStorage.setItem('currentAgendamentoSession', sessionId);
    this.shouldPersist = true;
  }

  endSession(): void {
    sessionStorage.removeItem('agendamentoSessionId');
    this.clearStorage();
    this.shouldPersist = false;
  }

  saveStepData(step: number, data: any): void {
    this.formData[`step${step}`] = data;
    this.formDataSubject.next(this.formData);

    if (this.shouldPersist) {
      localStorage.setItem('agendamentoForm', JSON.stringify(this.formData));
    }
  }

  getStepData(step: number): any {
    return this.formData[`step${step}`] || {};
  }

  getAllData(): any {
    return this.formData;
  }

  getMergedData(): any {
    const step1Data = this.getStepData(1);
    const step2Data = this.getStepData(2);

    return {
      ...step1Data,
      ...step2Data
    };
  }

  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
      this.currentStepSubject.next(this.currentStep);
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.currentStepSubject.next(this.currentStep);
    }
  }

  setCurrentStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) {
      this.currentStep = step;
      this.currentStepSubject.next(step);
    }
  }

  getCurrentStep(): number {
    return this.currentStep;
  }

  reset(): void {
    this.formData = {};
    this.currentStep = 1;
    this.formDataSubject.next(this.formData);
    this.currentStepSubject.next(this.currentStep);
    this.endSession();
  }

  isStepComplete(step: number): boolean {
    const stepData = this.formData[`step${step}`];
    return stepData && Object.keys(stepData).length > 0;
  }

  canProceed(step: number): boolean {
    const stepData = this.formData[`step${step}`] || {};

    switch (step) {
      case 1:
        return !!(
          stepData?.FK_CRGA &&
          stepData?.FK_TIPO_CRRT &&
          stepData?.DT_AGND &&
          stepData?.FK_JNLA &&
          stepData?.FK_MTRT_EPSA &&
          stepData?.FK_CAVA &&
          stepData?.FK_SRVC &&
          stepData?.FK_TPTD &&
          stepData?.FK_CLNT &&
          stepData?.FK_DTNR
        );

      case 2:
        const acompanhantes = stepData?.acompanhantes || [];
        return acompanhantes.length > 0;

      default:
        return false;
    }
  }
}
